import { useRef } from 'react';
import { Grid, GridItem, Button, Icon, Text, Menu, Portal, Badge, Checkmark } from '@chakra-ui/react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useContentState from './use-content-state';
import { Author, FieldOption } from '@/lib/types';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

interface Props {
  filterConfig: FilterConfig[];
  optionConfig: {
    [key: string]: {
      [key: string]: number;
    };
  };
  authors?: Author[];
  filters: any[];
  setFilters: (filters: any[]) => void;
  onFilterChange: (filters: { [key: string]: string[] }) => void;
  onResetFilters: () => void;
}

const Filters = ({
  filterConfig,
  optionConfig,
  authors,
  filters,
  setFilters,
  onFilterChange,
  onResetFilters,
}: Props) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { hasAuthor, setHasAuthor, selectedFilters, setSelectedFilters, clearSelectedFilters } =
    useContentState();

  function getSelectedCount(key: string): number {
    return (selectedFilters[key] || []).length;
  }

  const handleFilterChange = (key: string, value: string[]) => {
    const newFilters = { ...selectedFilters, [key]: value };
    setSelectedFilters(newFilters);
    onFilterChange(newFilters);
  };

  function handleResetFilters() {
    setFilters([{ key: 'status', val: 'published' }]);
    setHasAuthor(false);
    onFilterChange({});
    clearSelectedFilters();
    onResetFilters();
  }

  function changeAuthorFilter(val: string) {
    setFilters([
      { key: 'created_by', val: val },
      { key: 'status', val: 'published' },
    ]);
    setHasAuthor(true);
    clearSelectedFilters();
    onResetFilters();
  }

  return (
    <Grid
      templateColumns={{
        base: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(5, 1fr)',
        xl: 'repeat(6, 1fr)',
      }}
      gap={3}
      ref={contentRef}
    >
      {authors && authors.length > 0 && (
        <GridItem w='100%'>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant={'outline'}
                size={'sm'}
                w={'100%'}
                textAlign={'left'}
                justifyContent={'flex-start'}
              >
                {filters.find((filter) => filter.key === 'created_by')
                  ? authors.find(
                      (author) =>
                        author.user_id.toString() ===
                        filters.find((filter) => filter.key === 'created_by')?.val,
                    )?.first_name +
                    ' ' +
                    authors.find(
                      (author) =>
                        author.user_id.toString() ===
                        filters.find((filter) => filter.key === 'created_by')?.val,
                    )?.last_name
                  : 'Author'}
                <Icon as={ChevronDownIcon} w={4} h={4} ml='auto' />
              </Button>
            </Menu.Trigger>
            <Portal container={contentRef}>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.ItemGroup>
                    {authors.map((author) => (
                      <Menu.RadioItemGroup
                        key={author.user_id}
                        value={filters.find((filter) => filter.key === 'created_by')?.val || ''}
                        onValueChange={(e) => {
                          changeAuthorFilter(e.value);
                        }}
                      >
                        <Menu.RadioItem value={author.user_id.toString()}>
                          <Menu.ItemIndicator
                            position='absolute'
                            insetStart='2'
                            top='50%'
                            transform='translateY(-50%)'
                          >
                            <Icon as={CheckIcon} boxSize='4' />
                          </Menu.ItemIndicator>
                          {author.first_name + ' ' + author.last_name}
                        </Menu.RadioItem>
                      </Menu.RadioItemGroup>
                    ))}
                  </Menu.ItemGroup>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </GridItem>
      )}
      {/* Dropdown visibility and option rows are keyed off the configured field
          options (stable), NOT the live facet counts — facet_distribution is
          recomputed over the narrowed result set on every selection, so gating
          on it made unselected options (or whole dropdowns) vanish mid-click
          and close the open menu. */}
      {filterConfig.map(
        (filter) =>
          filter.options.length > 0 && (
            <GridItem w='100%' key={filter.id}>
              <Menu.Root>
                <Menu.Trigger asChild>
                  {(() => {
                    const count = getSelectedCount(filter.key);
                    const isActive = count > 0;
                    return (
                      <Button
                        variant={'outline'}
                        size={'sm'}
                        w={'100%'}
                        textAlign={'left'}
                        justifyContent={'flex-start'}
                        borderColor={isActive ? 'brand.solid' : undefined}
                        color={isActive ? 'brand.solid' : undefined}
                      >
                        {filter.label}
                        {isActive && (
                          <Badge size='sm' variant='solid' colorPalette='brand' borderRadius='full'>
                            {count}
                          </Badge>
                        )}
                        <Icon as={ChevronDownIcon} w={4} h={4} ml='auto' />
                      </Button>
                    );
                  })()}
                </Menu.Trigger>
                <Portal container={contentRef}>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.ItemGroup>
                        {filter.options.map((option) => {
                          const optionCount = optionConfig?.[filter.key]?.[option.option_value];
                          const isChecked =
                            selectedFilters[filter.key]?.includes(option.option_value) ?? false;
                          return (
                            <Menu.CheckboxItem
                              key={option.id}
                              value={option.option_value}
                              checked={isChecked}
                              closeOnSelect={false}
                              onCheckedChange={(checked) => {
                                const currentValues = selectedFilters[filter.key] || [];
                                const newValues = checked
                                  ? [...currentValues, option.option_value]
                                  : currentValues.filter((v) => v !== option.option_value);
                                handleFilterChange(filter.key, newValues);
                              }}
                            >
                              {/* Persistent checkbox square in the gutter the menu recipe
                                  reserves for [data-type] items (ps: 8). */}
                              <Checkmark
                                checked={isChecked}
                                size='sm'
                                colorPalette='brand'
                                position='absolute'
                                insetStart='2'
                                top='50%'
                                transform='translateY(-50%)'
                              />
                              {option.option_label}
                              {!!optionCount && (
                                <Text as={'b'} fontWeight={'medium'} ml={1}>
                                  ({optionCount})
                                </Text>
                              )}
                            </Menu.CheckboxItem>
                          );
                        })}
                      </Menu.ItemGroup>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </GridItem>
          ),
      )}
      {hasAuthor || (selectedFilters && Object.keys(selectedFilters).length) ? (
        <Button
          aria-label={'Reset filters'}
          variant={'ghost'}
          size={'sm'}
          w={'90px'}
          onClick={handleResetFilters}
        >
          Reset
          <Icon as={XMarkIcon} w={4} h={4} />
        </Button>
      ) : null}
    </Grid>
  );
};

export default Filters;
